import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { signInWithEmailAndPassword, signInWithPopup, GoogleAuthProvider } from 'firebase/auth';
import { showSuccess, showError, showWarning } from '../utils/toast';

const StudentLogin = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const checkStudentStatus = async (email) => {
    try {
      const studentQuery = query(collection(db, 'students'), where('email', '==', email));
      const studentSnapshot = await getDocs(studentQuery);

      if (studentSnapshot.empty) {
        showError('Student account not found. Please register first.');
        await auth.signOut();
        return false;
      }

      const studentData = studentSnapshot.docs[0].data();
      const studentDocId = studentSnapshot.docs[0].id;

      // Check approval status
      if (studentData.approvalStatus === 'pending') {
        showWarning('Your account is pending approval from your organization admin.');
        await auth.signOut();
        return false;
      }

      if (studentData.approvalStatus === 'rejected') {
        showError('Your account has been rejected. Please contact your organization admin.');
        await auth.signOut();
        return false;
      }

      if (studentData.approvalStatus === 'active' || studentData.approvalStatus === 'approved') {
        // Store student info in localStorage
        localStorage.setItem('isStudentAuthenticated', 'true');
        localStorage.setItem('studentId', studentDocId);
        localStorage.setItem('studentUid', studentData.uid);
        localStorage.setItem('studentName', studentData.name);
        localStorage.setItem('studentEmail', studentData.email);
        localStorage.setItem('studentOrganizationId', studentData.organizationId || '');
        localStorage.setItem('studentOrganizationName', studentData.organizationName || '');
        return true;
      }

      showError('Account status unknown. Please contact support.');
      await auth.signOut();
      return false;
    } catch (error) {
      console.error('Error checking student status:', error);
      showError('Error verifying account. Please try again.');
      await auth.signOut();
      return false;
    }
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();

    if (!formData.email || !formData.password) {
      showWarning('Please enter both email and password');
      return;
    }

    setLoading(true);

    try {
      // Sign in with email and password
      await signInWithEmailAndPassword(auth, formData.email, formData.password);

      // Check student status in Firestore
      const isApproved = await checkStudentStatus(formData.email);

      if (isApproved) {
        showSuccess('Login successful!');
        setTimeout(() => {
          navigate('/student-dashboard');
        }, 500);
      }
    } catch (error) {
      console.error('Error logging in:', error);
      
      if (error.code === 'auth/user-not-found') {
        showError('No account found with this email. Please register first.');
      } else if (error.code === 'auth/wrong-password') {
        showError('Incorrect password. Please try again.');
      } else if (error.code === 'auth/invalid-email') {
        showError('Invalid email address.');
      } else if (error.code === 'auth/too-many-requests') {
        showError('Too many failed attempts. Please try again later.');
      } else {
        showError('Login failed. Please check your credentials.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setLoading(true);

    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      // Check if student exists and is approved
      const isApproved = await checkStudentStatus(user.email);

      if (isApproved) {
        showSuccess('Login successful!');
        setTimeout(() => {
          navigate('/student-dashboard');
        }, 500);
      } else {
        // If student doesn't exist, redirect to registration
        const studentQuery = query(collection(db, 'students'), where('email', '==', user.email));
        const studentSnapshot = await getDocs(studentQuery);
        
        if (studentSnapshot.empty) {
          showWarning('No account found. Redirecting to registration...');
          setTimeout(() => {
            navigate('/student-register');
          }, 1500);
        }
      }
    } catch (error) {
      console.error('Error with Google login:', error);
      showError('Google login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 flex items-center justify-center py-12 px-4">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-8 text-white text-center">
            <div className="bg-white p-3 rounded-full w-20 h-20 mx-auto mb-4 flex items-center justify-center">
              <svg className="w-12 h-12 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            </div>
            <h1 className="text-3xl font-bold mb-2">Student Login</h1>
            <p className="text-indigo-100">Access your exams and results</p>
          </div>

          <div className="p-8">
            {/* Google Sign In Button */}
            <button
              type="button"
              onClick={handleGoogleLogin}
              disabled={loading}
              className="w-full flex items-center justify-center gap-3 bg-white border-2 border-gray-300 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-50 hover:border-indigo-500 transition disabled:opacity-50 mb-6"
            >
              <svg className="w-6 h-6" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </button>

            <div className="relative mb-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-gray-500">Or login with email</span>
              </div>
            </div>

            {/* Email Login Form */}
            <form onSubmit={handleEmailLogin} className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                  placeholder="your.email@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Password
                </label>
                <input
                  type="password"
                  name="password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                  placeholder="Enter your password"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-6 py-4 rounded-lg font-bold text-lg hover:shadow-xl transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Logging in...' : 'Login'}
              </button>
            </form>

            {/* Registration Link */}
            <div className="mt-6 text-center">
              <p className="text-gray-600">
                Don't have an account?{' '}
                <Link to="/student-register" className="text-indigo-600 font-semibold hover:text-indigo-800">
                  Register here
                </Link>
              </p>
            </div>

            {/* Back to Home */}
            <div className="mt-4 text-center">
              <Link to="/" className="text-gray-500 text-sm hover:text-gray-700">
                ← Back to Home
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentLogin;
